/* Next */
import { useRouter } from 'next/router'

/* React */
import { useEffect, useMemo, useState } from 'react'

/* Redux */
import SucheRedux from './Suche.Redux'

/* Material */
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { alpha, Theme } from '@mui/material/styles'

/* Material-Icons */
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'

/* Types */
import { item, tMenu } from 'src/shared/frontendTypes/navigation'
import { tProductPreview } from 'src/shared/frontendTypes/product'
import { MenuPageBuilder } from 'src/shared/dbTypes/menuPageBuilder'

/* Functions */
import { forbiddenSearchChars } from 'src/functions/regex'
import { post } from 'src/functions/fetch'
import getImageUrl from 'src/pages/Product/functions/imageUrl'
import getImageAlt from 'src/pages/Product/functions/imageAlt'

/* Components */
import AiGeneratedBadge from 'src/components/AiGeneratedBadge'
import LoadingIcon from 'src/components/LoadingIcon'

/* Config */
import config from 'src/config'
import { hrefForBrandPath, hrefForNavigationItem } from 'src/functions/navigationBreadcrumbs'
import productUrl, { productVariantUrl } from 'src/functions/productUrl'
import useNavigationPaths from 'src/layout/navigationPaths'
import { resolvePageBuilder } from 'src/pageBuilder/config'
import { isFilterProductsResponse } from 'src/shared/frontendTypes/productsFilter'
import type { brandSuggestion as tBrandSuggestion } from 'src/shared/frontendTypes/productsFilter'
import {
  createLandingPageImageAiGeneratedMatcher,
  type LandingPageImageAiGenerated,
} from 'src/functions/landingPageImageAiGenerated'

interface Props {
  open: boolean
  pageBuilder?: MenuPageBuilder
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}

interface SearchCategory {
  label: string
  href: string
  imageSrc?: string
  imageAlt?: string
  type: 'category' | 'brand'
}

const getEntryHref = (entry: item) => {
  return hrefForNavigationItem(entry) || '#'
}

const dedupeCategories = (items: SearchCategory[]) => {
  const seen = new Set<string>()

  return items.filter(item => {
    const key = `${item.label}-${item.href}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

const getSearchCategories = (
  pageBuilder: MenuPageBuilder | undefined,
  paths: tMenu
): SearchCategory[] => {
  const builderSearchItems = resolvePageBuilder(pageBuilder).search.items || []
  if (builderSearchItems.length > 0) {
    return dedupeCategories(builderSearchItems.map(item => ({
      label: item.label,
      href: item.href,
      imageSrc: item.imageSrc,
      imageAlt: item.imageAlt,
      type: item.href.startsWith('/b/') ? 'brand' as const : 'category' as const,
    }))).slice(0, 12)
  }

  const fromBuilder = resolvePageBuilder(pageBuilder).blocks.reduce<SearchCategory[]>((acc, block) => {
    if (block.type === 'categoryList' || block.type === 'categorySlider') {
      acc.push(...block.items.map(item => ({
        label: item.label,
        href: item.href,
        imageSrc: item.imageSrc,
        imageAlt: item.imageAlt,
        type: 'category' as const,
      })))

      return acc
    }

    if (block.type === 'brandList') {
      acc.push(...(block.items || []).map(item => ({
        label: item.label,
        href: item.href,
        imageSrc: item.imageSrc,
        imageAlt: item.imageAlt,
        type: 'brand' as const,
      })))

      return acc
    }

    return acc
  }, [])

  if (fromBuilder.length > 0) return dedupeCategories(fromBuilder).slice(0, 8)

  return dedupeCategories(paths
    .map(entry => ({
      label: entry.title,
      href: getEntryHref(entry),
      type: entry.brand ? 'brand' as const : 'category' as const,
    }))
    .filter(entry => entry.href !== '#')
  ).slice(0, 8)
}

const formatSucheInput = (suche: string) => suche
  .substring(0, 255)
  .replace(forbiddenSearchChars, '')

const saveSuche = (suche: string) => formatSucheInput(suche)
  .replace(/\s+/g, ' ')
  .trim()

const formatPrice = (value: number) =>
  new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(value)

const getDisplayPrice = (product: tProductPreview) =>
  typeof product.discount?.price === 'number'
    ? product.discount.price
    : product.price

const getSearchProductUrl = (product: tProductPreview) => {
  const href = productUrl(product.id, product.path)
  const combinationKey = String(product.key || '').trim()

  if (combinationKey && combinationKey !== String(product.id)) {
    return productVariantUrl(product.id, product.path, combinationKey)
  }

  if (product.options && product.options.length > 0) {
    return productUrl(product.id, product.path, product.options)
  }

  return href
}

const getBrandLogoUrl = (logo?: string) => {
  if (!logo) return ''
  if (/^https?:\/\//i.test(logo)) return logo
  if (logo.startsWith('/')) return logo

  return `${config.image_server}/brand/${logo}`
}

const getBrowseTitle = (
  showCategories: boolean,
  showBrands: boolean
) => {
  if (showCategories && showBrands) return 'Stöbere in Kategorien & Marken'
  if (showBrands) return 'Stöbere in unseren Marken'
  return 'Stöbere in unseren Kategorien'
}

const Suche: React.FC<Props> = ({ open, pageBuilder, landingPageImageAiGenerated }) => {
  const router = useRouter()
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))
  const paths = useNavigationPaths()
  const builderConfig = useMemo(
    () => resolvePageBuilder(pageBuilder),
    [pageBuilder]
  )
  const isAiGeneratedLandingImage = useMemo(
    () => createLandingPageImageAiGeneratedMatcher(landingPageImageAiGenerated),
    [landingPageImageAiGenerated],
  )

  const { propsSuche, updateSuche, closeSearchOverlay } = SucheRedux()

  const [query, setQuery] = useState(propsSuche || '')
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<tProductPreview[]>([])
  const [brandSuggestion, setBrandSuggestion] = useState<tBrandSuggestion | null>(null)
  const [hasRequested, setHasRequested] = useState(false)

  const categories = useMemo(
    () => getSearchCategories(pageBuilder, paths),
    [pageBuilder, paths]
  )
  const browseItems = useMemo(
    () => categories.filter(item =>
      item.type === 'category'
        ? builderConfig.search.showCategories
        : builderConfig.search.showBrands
    ),
    [builderConfig.search.showBrands, builderConfig.search.showCategories, categories]
  )

  const popularTerms = useMemo(
    () => browseItems.slice(0, 5).map(item => item.label),
    [browseItems]
  )

  useEffect(() => {
    if (!open) return

    setQuery(propsSuche || '')
    setResults([])
    setBrandSuggestion(null)
    setLoading(false)
    setHasRequested(false)
  }, [open, propsSuche])

  useEffect(() => {
    if (!open) return

    const savedQuery = saveSuche(query)
    if (savedQuery.length < 2) {
      setLoading(false)
      setResults([])
      setBrandSuggestion(null)
      setHasRequested(false)
      return
    }

    const controller = new AbortController()
    if (!builderConfig.search.showProducts) {
      setLoading(false)
      setResults([])
      setBrandSuggestion(null)
      setHasRequested(true)
      return () => controller.abort()
    }

    const timeout = window.setTimeout(async () => {
      setLoading(true)

      const data = await post({
        url: 'products',
        body: {
          ...config.shop,
          size: 4,
          page: 1,
          suche: savedQuery,
          fuzzy: false,
        },
        signal: controller.signal,
      })

      if (!isFilterProductsResponse(data)) {
        setResults([])
        setBrandSuggestion(null)
        setHasRequested(true)
        setLoading(false)
        return
      }

      setResults(data.filterProducts.products || [])
      setBrandSuggestion(data.brandSuggestion || null)
      setHasRequested(true)
      setLoading(false)
    }, 240)

    return () => {
      window.clearTimeout(timeout)
      controller.abort()
    }
  }, [builderConfig.search.showProducts, open, query])

  const handleClose = () => {
    closeSearchOverlay()
  }

  const submitSearch = (nextQuery = query) => {
    const savedQuery = saveSuche(nextQuery)
    updateSuche(savedQuery)
    closeSearchOverlay()

    if (!savedQuery) {
      router.push('/suche')
      return
    }

    router.push({
      pathname: '/suche',
      query: { suche: savedQuery },
    })
  }

  const openProduct = (product: tProductPreview) => {
    const href = getSearchProductUrl(product)
    closeSearchOverlay()
    window.location.href = href
  }

  const openCategory = (href: string) => {
    closeSearchOverlay()
    router.push(href)
  }

  const openBrandSuggestion = (brand: tBrandSuggestion) => {
    closeSearchOverlay()
    router.push(hrefForBrandPath(brand.path, brand.id) || '/brands')
  }

  const renderCategoryGrid = () =>
    <Box
      sx={{
        display: 'grid',
        gap: isMobile ? 1.5 : 2,
        gridTemplateColumns: isMobile
          ? 'repeat(2, minmax(0, 1fr))'
          : 'repeat(4, minmax(0, 1fr))',
      }}
    >
      {browseItems.map(category => <Box
        key={`${category.label}-${category.href}`}
        component='button'
        type='button'
        onClick={() => openCategory(category.href)}
        sx={{
          position: 'relative',
          border: 'none',
          padding: 0,
          overflow: 'hidden',
          borderRadius: 3,
          background: alpha('#ffffff', 0.7),
          cursor: 'pointer',
          textAlign: 'left',
          minHeight: isMobile ? 180 : 220,
          boxShadow: '0 18px 42px rgba(15, 23, 42, 0.08)',
        }}
      >
        {category.imageSrc
          ? <>
              <Box
                component='img'
                src={category.imageSrc}
                alt={category.imageAlt || category.label}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
              <AiGeneratedBadge
                aiGenerated={isAiGeneratedLandingImage(category.imageSrc)}
                size='compact'
                placement='top-left'
              />
            </>
          : <Box
              sx={{
                width: '100%',
                height: '100%',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.95), rgba(242, 235, 227, 0.95))',
              }}
            />
        }

        <Box
          sx={{
            position: 'absolute',
            left: 14,
            right: 14,
            bottom: 14,
            display: 'inline-flex',
            justifyContent: 'center',
            borderRadius: 999,
            padding: '10px 16px',
            background: alpha('#ffffff', 0.92),
            backdropFilter: 'blur(14px)',
            color: 'var(--page-builder-nav-text, #1f2933)',
            fontSize: isMobile ? 16 : 18,
            fontWeight: 500,
            boxShadow: '0 10px 24px rgba(15, 23, 42, 0.12)',
          }}
        >
          {category.label}
        </Box>
      </Box>)}
    </Box>

  const renderResults = () =>
    <Box sx={{ display: 'grid', gap: 2 }}>
      {results.map(product => <Box
        key={product.key}
        component='button'
        type='button'
        onClick={() => openProduct(product)}
        sx={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '88px 1fr auto' : '108px 1fr auto',
          gap: 2,
          alignItems: 'center',
          width: '100%',
          border: 'none',
          borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
          background: 'transparent',
          padding: 0,
          paddingBottom: 2,
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: isMobile ? 88 : 108,
            height: isMobile ? 88 : 108,
          }}
        >
          <Box
            component='img'
            src={getImageUrl(product.images?.[0], product.id, 'sm') || ''}
            alt={getImageAlt(product.images?.[0], product.metaTitle)}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              borderRadius: 2,
              background: '#f8f6f2',
              padding: 1,
            }}
          />
          <AiGeneratedBadge aiGenerated={product.images?.[0]?.aiGenerated} size='compact' />
        </Box>

        <Box sx={{ display: 'grid', gap: .75 }}>
          <Typography sx={{ fontSize: isMobile ? 20 : 24, fontWeight: 500 }}>
            {product.name}
          </Typography>
          <Typography sx={{ color: 'var(--page-builder-muted, #667085)', fontSize: isMobile ? 16 : 18 }}>
            {formatPrice(getDisplayPrice(product))}
          </Typography>
        </Box>

        <IconButton
          aria-label='Zur Suche'
          sx={{
            width: 44,
            height: 44,
            background: '#f1f1f1',
            color: 'var(--page-builder-nav-text, #1f2933)',
          }}
        >
          <ArrowForwardIcon />
        </IconButton>
      </Box>)}
    </Box>

  const renderBrandSuggestion = (brand: tBrandSuggestion) =>
    <Box
      component='button'
      type='button'
      onClick={() => openBrandSuggestion(brand)}
      sx={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '88px 1fr auto' : '108px 1fr auto',
        gap: 2,
        alignItems: 'center',
        width: '100%',
        border: '1px solid rgba(15, 23, 42, 0.10)',
        borderRadius: 2,
        background: alpha('#ffffff', 0.72),
        padding: isMobile ? 1.5 : 2,
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <Box
        sx={{
          width: isMobile ? 88 : 108,
          height: isMobile ? 72 : 86,
          borderRadius: 2,
          background: '#f8f6f2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {brand.logo
          ? <>
            <Box
              component='img'
              src={getBrandLogoUrl(brand.logo)}
              alt={brand.titel}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                padding: 1,
              }}
            />
            <AiGeneratedBadge
              aiGenerated={isAiGeneratedLandingImage(getBrandLogoUrl(brand.logo))}
              size='compact'
              placement='top-left'
            />
          </>
          : <Typography sx={{ fontSize: 22, fontWeight: 500 }}>
              {brand.titel.slice(0, 1)}
            </Typography>
        }
      </Box>

      <Box sx={{ display: 'grid', gap: .5 }}>
        <Typography sx={{ fontSize: isMobile ? 16 : 18, color: 'var(--page-builder-muted, #667085)' }}>
          Marke
        </Typography>
        <Typography sx={{ fontSize: isMobile ? 22 : 26, fontWeight: 500 }}>
          {brand.titel}
        </Typography>
      </Box>

      <IconButton
        aria-label='Marke öffnen'
        sx={{
          width: 44,
          height: 44,
          background: '#f1f1f1',
          color: 'var(--page-builder-nav-text, #1f2933)',
        }}
      >
        <ArrowForwardIcon />
      </IconButton>
    </Box>

  return <Dialog
    open={open}
    onClose={handleClose}
    fullWidth
    maxWidth='lg'
    PaperProps={{
      sx: {
        width: 'min(1120px, calc(100% - 24px))',
        margin: isMobile ? '12px' : '28px auto',
        borderRadius: isMobile ? 3 : 4,
        background: alpha('#ffffff', 0.92),
        backdropFilter: 'blur(22px)',
        boxShadow: '0 32px 90px rgba(15, 23, 42, 0.18)',
      },
    }}
    BackdropProps={{
      sx: {
        backgroundColor: 'rgba(15, 23, 42, 0.2)',
        backdropFilter: 'blur(10px)',
      },
    }}
  >
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr auto auto',
          alignItems: 'center',
          gap: 1.5,
          pb: 2,
        }}
      >
        <SearchIcon sx={{ color: 'var(--page-builder-muted, #667085)', fontSize: isMobile ? 28 : 34 }} />

        <InputBase
          value={query}
          autoFocus
          placeholder='Suchen'
          onChange={({ target: { value } }) => setQuery(formatSucheInput(value))}
          onKeyDown={event => {
            if (event.key !== 'Enter') return
            event.preventDefault()
            submitSearch()
          }}
          sx={{
            fontSize: isMobile ? 22 : 28,
            lineHeight: 1.2,
          }}
        />

        <IconButton
          aria-label='Suche öffnen'
          onClick={() => submitSearch()}
          sx={{ color: 'var(--page-builder-muted, #667085)' }}
        >
          <ArrowForwardIcon />
        </IconButton>

        <IconButton
          aria-label='Suche schließen'
          onClick={handleClose}
          sx={{ color: 'var(--page-builder-muted, #667085)' }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      <Box
        sx={{
          pt: isMobile ? 2 : 3,
          pb: 1,
          display: 'grid',
          gap: isMobile ? 3 : 4,
        }}
      >
        {saveSuche(query).length < 2 &&
          <>
            {popularTerms.length > 0 &&
              <Box sx={{ display: 'grid', gap: 1.5 }}>
                <Typography
                  sx={{
                    color: 'var(--page-builder-muted, #667085)',
                    fontSize: isMobile ? 16 : 18,
                    letterSpacing: '.06em',
                    textTransform: 'uppercase',
                  }}
                >
                  Beliebte Suchbegriffe
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {popularTerms.map(term => <Chip
                    key={term}
                    label={term}
                    onClick={() => setQuery(term)}
                    sx={{
                      height: isMobile ? 44 : 50,
                      borderRadius: 999,
                      background: '#f2f2f2',
                      fontSize: isMobile ? 18 : 20,
                    }}
                  />)}
                </Box>
              </Box>
            }

            {browseItems.length > 0 &&
              <Box sx={{ display: 'grid', gap: 1.5 }}>
              <Typography
                sx={{
                  color: 'var(--page-builder-muted, #667085)',
                  fontSize: isMobile ? 16 : 18,
                  letterSpacing: '.06em',
                  textTransform: 'uppercase',
                }}
              >
                {getBrowseTitle(builderConfig.search.showCategories, builderConfig.search.showBrands)}
              </Typography>

              {renderCategoryGrid()}
              </Box>
            }
          </>
        }

        {saveSuche(query).length >= 2 &&
          <>
            {loading &&
              <Box
                sx={{
                  minHeight: 180,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--page-builder-muted, #667085)',
                }}
              >
                <LoadingIcon />
              </Box>
            }

            {!loading && brandSuggestion &&
              <Box sx={{ display: 'grid', gap: 1.5 }}>
                <Typography
                  sx={{
                    color: 'var(--page-builder-muted, #667085)',
                    fontSize: isMobile ? 16 : 18,
                    letterSpacing: '.06em',
                    textTransform: 'uppercase',
                  }}
                >
                  Marke gefunden
                </Typography>

                {renderBrandSuggestion(brandSuggestion)}
              </Box>
            }

            {!loading && builderConfig.search.showProducts && results.length > 0 &&
              <Box sx={{ display: 'grid', gap: 1.5 }}>
                <Typography
                  sx={{
                    color: 'var(--page-builder-muted, #667085)',
                    fontSize: isMobile ? 16 : 18,
                    letterSpacing: '.06em',
                    textTransform: 'uppercase',
                  }}
                >
                  Passende Produkte
                </Typography>

                {renderResults()}
              </Box>
            }

            {!loading && hasRequested && !brandSuggestion && (!builderConfig.search.showProducts || results.length === 0) &&
              <>
                <Typography
                  sx={{
                    color: 'var(--page-builder-muted, #667085)',
                    fontSize: isMobile ? 20 : 24,
                    textAlign: 'center',
                  }}
                >
                  Leider konnten wir keine Ergebnisse für &quot;{saveSuche(query)}&quot; finden.
                </Typography>

                {browseItems.length > 0 &&
                  <Box sx={{ display: 'grid', gap: 1.5 }}>
                  <Typography
                    sx={{
                      color: 'var(--page-builder-muted, #667085)',
                      fontSize: isMobile ? 16 : 18,
                      letterSpacing: '.06em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {getBrowseTitle(builderConfig.search.showCategories, builderConfig.search.showBrands)}
                  </Typography>

                  {renderCategoryGrid()}
                  </Box>
                }
              </>
            }
          </>
        }
      </Box>
    </Box>
  </Dialog>
}

export default Suche
